<script setup lang="ts">
/**
 * One document in the guided flow: what it is, where to get it, what we
 * check, and the upload itself (with the number and expiry its type asks
 * for). A document already sent shows as done, with Replace when the server
 * would accept a replacement.
 */
import { computed, onBeforeUnmount, ref } from "vue";
import {
  useCompanyKyc,
  uploadKycDocument,
  canUploadType,
  awaitingLabel,
  reasonFrom,
  type KycDocumentType,
  type KycView,
} from "../../composables/useCompanyKyc";
import { guideFor, inSentence, GENERAL_TIPS } from "./docGuide";

const props = defineProps<{
  view: KycView;
  type: KycDocumentType;
  index: number;
  total: number;
}>();
const emit = defineEmits<{
  (e: "uploaded", submitted: boolean): void;
  (e: "next"): void;
  (e: "back"): void;
}>();

const kyc = useCompanyKyc();
const ACCEPT = ["application/pdf", "image/jpeg", "image/png"];
const MAX_BYTES = 10 * 1024 * 1024;

const doc = computed(() => props.type.document);
const rejected = computed(() => doc.value?.review_status === "rejected");
const expired = computed(() => !!doc.value?.expired || doc.value?.rejection_reason_code === "doc_expired");
const done = computed(() => !!doc.value && !rejected.value && !expired.value);
const allowed = computed(() => canUploadType(props.view, props.type));
const replacing = ref(false);
const showForm = computed(() => allowed.value && (!done.value || replacing.value));
const guide = computed(() => guideFor(props.type.doc_type, props.type.label));

// The server sends everything for review as soon as the last required
// document is in, while the company is still sending its documents
// (companyKycModel.addDocument). Say so on that step; optional documents can
// still be added at any time afterwards.
const live = (t: KycDocumentType) =>
  !!t.document && t.document.review_status !== "rejected" && !t.document.expired;
const sendsForReview = computed(
  () =>
    props.type.is_required &&
    ["pending", "rejected"].includes(props.view.company.kyc_status || "") &&
    props.view.document_types.every(
      (t) => t.doc_type === props.type.doc_type || ((!t.is_required || live(t)) && t.document?.review_status !== "rejected"),
    ),
);

const onFile = computed(() => {
  const c = props.view.company;
  if (props.type.doc_type === "kra_pin_certificate" && c.kra_pin) return { label: "KRA PIN you gave at signup", value: c.kra_pin };
  if (props.type.doc_type === "certificate_of_incorporation" && c.registration_number) {
    return { label: "Registration number you gave at signup", value: c.registration_number };
  }
  return null;
});

const fmtDay = (ymd: string | null) =>
  ymd ? new Date(`${ymd}T00:00:00`).toLocaleDateString("en-KE", { day: "numeric", month: "long", year: "numeric" }) : "";
const fmtBytes = (n: number) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const tomorrow = (() => {
  const d = new Date(Date.now() + 3 * 3600e3 + 86400e3);
  return d.toISOString().slice(0, 10);
})();

// ─── The form ────────────────────────────────────────────────────────
const file = ref<File | null>(null);
const previewUrl = ref("");
const number = ref("");
const expiry = ref("");
const busy = ref(false);
const progress = ref(0);
const error = ref("");
const success = ref(false);
const dragging = ref(false);

function clearPreview() {
  if (previewUrl.value) URL.revokeObjectURL(previewUrl.value);
  previewUrl.value = "";
}
onBeforeUnmount(clearPreview);

function take(f: File | null | undefined) {
  error.value = "";
  if (!f) return;
  if (f.type && !ACCEPT.includes(f.type)) {
    error.value = "That file type isn't accepted. Choose a PDF, JPG or PNG.";
    return;
  }
  if (f.size > MAX_BYTES) {
    error.value = `That file is ${fmtBytes(f.size)}. Files can be up to 10 MB — try a PDF or a smaller photo.`;
    return;
  }
  clearPreview();
  file.value = f;
  if (f.type.startsWith("image/")) previewUrl.value = URL.createObjectURL(f);
}
const onPick = (e: Event) => {
  take((e.target as HTMLInputElement).files?.[0]);
  (e.target as HTMLInputElement).value = "";
};
const onDrop = (e: DragEvent) => {
  dragging.value = false;
  take(e.dataTransfer?.files?.[0]);
};
function removeFile() {
  file.value = null;
  clearPreview();
}

const missing = computed(() => {
  if (!file.value) return "Choose the file to upload.";
  if (props.type.requires_number && !number.value.trim()) return `Enter the number on the ${inSentence(props.type.label)}.`;
  if (props.type.requires_expiry && !expiry.value) return "Enter the expiry date.";
  if (props.type.requires_expiry && expiry.value < tomorrow) return "That date has passed. Upload a document that is still valid.";
  return "";
});

async function submit() {
  if (missing.value) {
    error.value = missing.value;
    return;
  }
  busy.value = true;
  error.value = "";
  progress.value = 0;
  try {
    const res = await uploadKycDocument({
      docType: props.type.doc_type,
      file: file.value!,
      documentNumber: props.type.requires_number ? number.value.trim() : undefined,
      expiresAt: props.type.requires_expiry ? expiry.value : undefined,
      onProgress: (p) => (progress.value = p),
    });
    kyc.adopt(res.view);
    success.value = true;
    // A beat on the confirmation before moving on.
    setTimeout(() => emit("uploaded", res.submitted), 900);
  } catch (err) {
    error.value = reasonFrom(err, "The upload didn't go through. Check your connection and try again.");
    void kyc.load(true);
  } finally {
    busy.value = false;
  }
}

const statusOf = computed(() => {
  const d = doc.value;
  if (!d) return "";
  if (d.review_status === "approved") return "Accepted";
  return awaitingLabel(props.view.company.kyc_status, props.type.is_required);
});
// After an upload Xobo checks it: say so, unless it waits for the company to
// send its documents (then they are checked together when it is sent).
const awaitsXobo = computed(() => awaitingLabel(props.view.company.kyc_status, props.type.is_required) === "Pending verification");
</script>

<template>
  <section class="ds vf-card">
    <header class="ds-head">
      <div class="ds-head__meta">
        <span class="vf-eyebrow">Step {{ index + 1 }} of {{ total }}</span>
        <span class="ds-pill" :class="type.is_required ? 'is-req' : 'is-opt'">{{ type.is_required ? "Required" : "Optional" }}</span>
      </div>
      <h2 class="ds-title" tabindex="-1">{{ type.label }}</h2>
      <p class="ds-what">{{ guide.what }}</p>
    </header>

    <div v-if="rejected && !expired" class="ds-banner is-bad">
      <strong>We couldn't accept the last one: {{ doc?.rejection_reason_label || "Not accepted" }}.</strong>
      <span v-if="doc?.rejection_note">{{ doc.rejection_note }}</span>
    </div>
    <div v-else-if="expired" class="ds-banner is-bad">
      <strong>The one we have expired{{ doc?.expires_at ? ` on ${fmtDay(doc.expires_at)}` : "" }}.</strong>
      <span>Upload the current one to stay verified.</span>
    </div>

    <!-- Already sent -->
    <div v-if="done && !replacing && !success" class="ds-done">
      <span class="ds-done__icon" aria-hidden="true">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
      </span>
      <div class="ds-done__text">
        <strong>{{ doc?.uploaded_by_staff ? "Xobo added this one for you" : "You've sent this one" }}</strong>
        <span>
          {{ doc?.original_filename || "File" }} · {{ doc ? fmtBytes(doc.bytes) : "" }}
          <template v-if="doc?.uploaded_by_name && !doc.uploaded_by_staff"> · by {{ doc.uploaded_by_name }}</template>
        </span>
        <span v-if="doc?.document_number || doc?.expires_at">
          <template v-if="doc?.document_number">No. {{ doc.document_number }}</template>
          <template v-if="doc?.document_number && doc?.expires_at"> · </template>
          <template v-if="doc?.expires_at">Valid until {{ fmtDay(doc.expires_at) }}</template>
        </span>
      </div>
      <span class="ds-done__status">{{ statusOf }}</span>
    </div>

    <!-- Can't be changed right now -->
    <p v-else-if="!allowed && !success" class="ds-banner is-info">
      This one can't be changed while we review your documents. If something is wrong with it, we'll tell you.
    </p>

    <!-- Upload -->
    <div v-if="showForm || success" class="ds-grid">
      <div class="ds-form">
        <div v-if="success" class="ds-success" role="status">
          <span class="ds-done__icon" aria-hidden="true">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
          </span>
          <div class="ds-success__text">
            <strong>Uploaded</strong>
            <span v-if="awaitsXobo">Pending verification — Xobo will check it.</span>
          </div>
        </div>

        <template v-else>
          <div
            class="ds-drop"
            :class="{ 'is-drag': dragging, 'has-file': file }"
            @dragenter.prevent="dragging = true"
            @dragover.prevent="dragging = true"
            @dragleave.prevent="dragging = false"
            @drop.prevent="onDrop"
          >
            <template v-if="!file">
              <span class="ds-drop__icon" aria-hidden="true">
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>
              </span>
              <p class="ds-drop__lead">
                <span class="ds-drop__lead--drag">Drag the file here, or</span>
                <span class="ds-drop__lead--touch">Add the file</span>
              </p>
              <div class="ds-drop__buttons">
                <label class="vf-btn vf-btn--ghost ds-drop__pick">
                  Choose a file
                  <input :id="`f-${type.doc_type}`" type="file" accept="application/pdf,image/jpeg,image/png" class="ds-hidden" @change="onPick" />
                </label>
                <label class="vf-btn vf-btn--ghost ds-drop__camera">
                  Take a photo
                  <input type="file" accept="image/*" capture="environment" class="ds-hidden" @change="onPick" />
                </label>
              </div>
              <p class="ds-drop__hint">PDF, JPG or PNG · up to 10 MB</p>
            </template>
            <div v-else class="ds-file">
              <img v-if="previewUrl" :src="previewUrl" alt="" class="ds-file__thumb" />
              <span v-else class="ds-file__pdf" aria-hidden="true">PDF</span>
              <div class="ds-file__meta">
                <strong>{{ file.name }}</strong>
                <span>{{ fmtBytes(file.size) }}</span>
              </div>
              <button type="button" class="ds-file__remove" :disabled="busy" @click="removeFile">Remove</button>
            </div>
          </div>

          <div v-if="type.requires_number || type.requires_expiry" class="ds-fields">
            <label v-if="type.requires_number" class="ds-field">
              <span>Number on the document</span>
              <input :id="`n-${type.doc_type}`" v-model="number" type="text" maxlength="80" autocomplete="off" placeholder="As printed on it" />
            </label>
            <label v-if="type.requires_expiry" class="ds-field">
              <span>Valid until</span>
              <input :id="`e-${type.doc_type}`" v-model="expiry" type="date" :min="tomorrow" />
            </label>
          </div>

          <div v-if="sendsForReview && !busy" class="ds-last">
            <p>
              <strong>This is the last required document.</strong>
              Uploading it sends everything to Xobo for review. Optional documents can be added at any time,
              now or later.
            </p>
          </div>

          <div v-if="busy" class="ds-progress" role="status" aria-live="polite">
            <div class="ds-progress__bar"><span :style="{ width: progress + '%' }"></span></div>
            <span>Uploading {{ progress }}%</span>
          </div>
          <p v-if="error" class="ds-error" role="alert">{{ error }}</p>
        </template>
      </div>

      <aside class="ds-help">
        <h3>Before you upload, check</h3>
        <ul>
          <li v-for="c in guide.check" :key="c">{{ c }}</li>
          <li v-for="t in GENERAL_TIPS" :key="t">{{ t }}</li>
        </ul>
        <p v-if="onFile" class="ds-onfile">
          {{ onFile.label }}: <strong>{{ onFile.value }}</strong>
        </p>
        <template v-if="guide.where">
          <h3>Where to get it</h3>
          <p>{{ guide.where }}</p>
        </template>
      </aside>
    </div>

    <footer class="ds-actions">
      <button type="button" class="vf-btn vf-btn--link" :disabled="busy" @click="emit('back')">Back</button>
      <div class="ds-actions__right">
        <template v-if="showForm && !success">
          <button v-if="replacing" type="button" class="vf-btn vf-btn--ghost" :disabled="busy" @click="replacing = false; removeFile()">Keep the current one</button>
          <button v-else-if="!type.is_required" type="button" class="vf-btn vf-btn--ghost" :disabled="busy" @click="emit('next')">Skip this one</button>
          <button type="button" class="vf-btn" :disabled="busy" @click="submit">
            {{ busy ? "Uploading…" : "Upload and continue" }}
          </button>
        </template>
        <template v-else-if="!success">
          <button v-if="done && allowed" type="button" class="vf-btn vf-btn--ghost" @click="replacing = true">Replace file</button>
          <button type="button" class="vf-btn" @click="emit('next')">Continue</button>
        </template>
      </div>
    </footer>
  </section>
</template>

<style scoped>
.ds { padding: 32px 36px 28px; display: flex; flex-direction: column; gap: 22px; }
.ds-head__meta { display: flex; align-items: center; gap: 10px; }
.ds-head__meta .vf-eyebrow { margin: 0; }
.ds-pill { padding: 2px 9px; border-radius: 999px; font-size: 0.7rem; font-weight: 700; }
.ds-pill.is-req { background: var(--vf-info-bg); color: var(--vf-navy); }
.ds-pill.is-opt { background: #f1f4f8; color: var(--vf-muted); }
.ds-title { margin: 10px 0 0; font-size: 1.55rem; line-height: 1.25; font-weight: 700; color: var(--vf-navy); letter-spacing: -0.01em; }
.ds-what { margin: 6px 0 0; font-size: 0.98rem; color: var(--vf-text); max-width: 60ch; }

.ds-banner {
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 14px 16px;
  border-radius: 12px;
  font-size: 0.9rem;
  line-height: 1.5;
}
.ds-banner.is-bad { background: var(--vf-bad-bg); border: 1px solid #f3c9c9; color: #7f1d1d; }
.ds-banner.is-bad strong { color: var(--vf-bad); }
.ds-banner.is-info { background: var(--vf-info-bg); color: var(--vf-navy); }

.ds-done {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px;
  border: 1px solid #cfe9d8;
  border-radius: 14px;
  background: var(--vf-ok-bg);
}
.ds-done__icon {
  width: 42px;
  height: 42px;
  flex-shrink: 0;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--vf-ok);
  color: #fff;
}
.ds-done__text { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; font-size: 0.86rem; color: var(--vf-text); overflow-wrap: anywhere; }
.ds-done__text strong { font-size: 0.98rem; color: var(--vf-ink); }
.ds-done__status { flex-shrink: 0; padding: 4px 10px; border-radius: 999px; background: #fff; border: 1px solid #cfe9d8; font-size: 0.76rem; font-weight: 700; color: var(--vf-ok); }

.ds-grid { display: grid; grid-template-columns: minmax(0, 1fr) 280px; gap: 24px; align-items: start; }
.ds-form { display: flex; flex-direction: column; gap: 16px; min-width: 0; }

.ds-drop {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 32px 20px;
  border: 2px dashed #c6d0de;
  border-radius: 16px;
  background: #fbfcfe;
  text-align: center;
  transition: border-color 0.15s, background 0.15s;
}
.ds-drop.is-drag { border-color: var(--vf-navy); background: var(--vf-info-bg); }
.ds-drop.has-file { padding: 16px; border-style: solid; border-color: #cfe9d8; background: #fff; }
.ds-drop__icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--vf-info-bg);
  color: var(--vf-navy);
}
.ds-drop__lead { margin: 0; font-size: 0.95rem; font-weight: 600; color: var(--vf-ink); }
.ds-drop__buttons { display: flex; flex-wrap: wrap; justify-content: center; gap: 10px; }
.ds-drop__pick, .ds-drop__camera { height: 42px; cursor: pointer; }
.ds-drop__pick:focus-within, .ds-drop__camera:focus-within { box-shadow: 0 0 0 3px rgba(18, 40, 74, 0.25); }
.ds-drop__camera { display: none; }
.ds-drop__lead--touch { display: none; }
.ds-drop__hint { margin: 0; font-size: 0.78rem; color: var(--vf-muted); }
.ds-hidden { position: absolute; width: 1px; height: 1px; opacity: 0; overflow: hidden; }

.ds-file { width: 100%; display: flex; align-items: center; gap: 14px; text-align: left; }
.ds-file__thumb { width: 64px; height: 64px; flex-shrink: 0; object-fit: cover; border-radius: 10px; border: 1px solid var(--vf-line); }
.ds-file__pdf {
  width: 64px;
  height: 64px;
  flex-shrink: 0;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--vf-bad-bg);
  color: var(--vf-bad);
  font-size: 0.8rem;
  font-weight: 800;
  letter-spacing: 0.05em;
}
.ds-file__meta { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.ds-file__meta strong { font-size: 0.92rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ds-file__meta span { font-size: 0.8rem; color: var(--vf-muted); }
.ds-file__remove { border: 0; background: none; font: inherit; font-size: 0.84rem; font-weight: 600; color: var(--vf-bad); cursor: pointer; }
.ds-file__remove:hover { text-decoration: underline; }

.ds-fields { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; }
.ds-field { display: flex; flex-direction: column; gap: 6px; font-size: 0.82rem; font-weight: 600; color: var(--vf-text); }
.ds-field input {
  height: 44px;
  padding: 0 14px;
  border: 1.5px solid var(--vf-line);
  border-radius: 10px;
  background: #fff;
  font: inherit;
  font-size: 0.92rem;
  font-weight: 500;
  color: var(--vf-ink);
}
.ds-field input:focus { outline: none; border-color: var(--vf-navy); box-shadow: 0 0 0 3px rgba(18, 40, 74, 0.1); }

.ds-last {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  padding: 14px 16px;
  border-radius: 12px;
  background: var(--vf-info-bg);
  color: var(--vf-ink);
  font-size: 0.88rem;
  line-height: 1.5;
}
.ds-last p { margin: 0; }
.ds-last strong { color: var(--vf-navy); }

.ds-progress { display: flex; align-items: center; gap: 12px; font-size: 0.82rem; font-weight: 600; color: var(--vf-text); }
.ds-progress__bar { flex: 1; height: 6px; border-radius: 999px; background: #dde3ec; overflow: hidden; }
.ds-progress__bar span { display: block; height: 100%; background: var(--vf-navy); transition: width 0.2s; }
.ds-error { margin: 0; padding: 10px 14px; border-radius: 10px; background: var(--vf-bad-bg); color: var(--vf-bad); font-size: 0.86rem; font-weight: 500; }

.ds-success { display: flex; align-items: center; gap: 12px; padding: 22px; border-radius: 16px; background: var(--vf-ok-bg); color: var(--vf-ok); font-size: 1rem; }
.ds-success__text { display: flex; flex-direction: column; gap: 2px; }
.ds-success__text span { font-size: 0.86rem; color: var(--vf-ink); }

.ds-help {
  padding: 18px 18px 6px;
  border-radius: 14px;
  background: #f7f9fc;
  border: 1px solid var(--vf-line);
}
.ds-help h3 { margin: 0 0 8px; font-size: 0.78rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: var(--vf-navy-2); }
.ds-help ul { margin: 0 0 14px; padding: 0; list-style: none; display: flex; flex-direction: column; gap: 7px; }
.ds-help li { position: relative; padding-left: 20px; font-size: 0.84rem; line-height: 1.45; color: var(--vf-text); }
.ds-help li::before {
  content: "";
  position: absolute;
  left: 2px;
  top: 6px;
  width: 9px;
  height: 5px;
  border-left: 2px solid var(--vf-ok);
  border-bottom: 2px solid var(--vf-ok);
  transform: rotate(-45deg);
}
.ds-help p { margin: 0 0 14px; font-size: 0.84rem; line-height: 1.5; color: var(--vf-text); }
.ds-onfile { padding: 10px 12px; border-radius: 10px; background: #fff; border: 1px solid var(--vf-line); }
.ds-onfile strong { color: var(--vf-ink); font-variant-numeric: tabular-nums; }

.ds-actions { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding-top: 18px; border-top: 1px solid var(--vf-line); }
.ds-actions__right { display: flex; flex-wrap: wrap; justify-content: flex-end; gap: 10px; }

@media (max-width: 900px) {
  .ds-grid { grid-template-columns: minmax(0, 1fr); }
}
@media (max-width: 640px) {
  .ds { padding: 22px 18px 20px; }
  .ds-title { font-size: 1.3rem; }
  .ds-fields { grid-template-columns: minmax(0, 1fr); }
  .ds-drop__camera { display: inline-flex; }
  .ds-drop__lead--drag { display: none; }
  .ds-drop__lead--touch { display: inline; }
  .ds-actions { flex-direction: column-reverse; align-items: stretch; }
  .ds-actions__right { flex-direction: column-reverse; }
  .ds-actions__right .vf-btn { width: 100%; }
}
</style>
